import { imageFormatRegistry } from '../format-registry';
import { DecodedImage, IImageFormat } from '../format-interface';
import { parseProjectData } from './serializer';
import { SerializedProject } from './types';

/**
 * @file project/importer.ts
 * @description Stateless logic for importing project or image data from binary buffers.
 * Does not modify global application state.
 */

/**
 * @function importProjectFromBuffer
 * @description Decodes a binary buffer into a structured image or project object.
 * Supports native .hcie (JSON) and other registered image formats.
 * @param {ArrayBuffer} buffer - The input file data.
 * @param {string} fileName - Original file name to determine extension.
 * @returns {Promise<SerializedProject | DecodedImage>} Decoded project or image data.
 * @throws {Error} If the format is unsupported or the buffer is malformed.
 */
export async function importProjectFromBuffer(
  buffer: ArrayBuffer,
  fileName: string
): Promise<SerializedProject | DecodedImage> {
  console.log(`[Importer] Decoding binary buffer for ${fileName}...`);
  const extension: string = "." + fileName.split(".").pop()?.toLowerCase();
  
  // CASE 1: Identify via Magic Numbers (Highest Reliability)
  let format: IImageFormat | null = imageFormatRegistry.identify(buffer);
  
  // CASE 2: Native HCIE project format fallback (JSON)
  if (!format && extension === ".hcie") {
    const decoder: TextDecoder = new TextDecoder();
    const jsonString: string = decoder.decode(buffer);
    return parseProjectData(jsonString);
  }

  // CASE 3: Standard image formats fallback by extension
  if (!format) {
    format = imageFormatRegistry.getByExtension(extension);
  }

  if (!format || !format.canRead) {
    throw new Error(`[Importer] Unsupported or unreadable file format: ${extension}`);
  }

  const decodedImage: DecodedImage = await format.read(buffer);
  if (!decodedImage || !decodedImage.layers || decodedImage.layers.length === 0) {
    throw new Error(`[Importer] Format adapter for ${format.name} returned empty or invalid data.`);
  }

  return decodedImage;
}
